export interface Tables
{
	tripCost: Record<string, number[]>;
	warehousePlaces: string[];
	noFreeDeliveryPlaces: string[];
	halfCostDeliveryPlaces: string[];
	routesToFreePassingTowns: Record<string, string[][]>;
	paidRoutes: Record<string, Record<string, number[]>>;
	railwayExceptions: string[];
	fullCostDeliveryPlaces: string[];
	newRegions: string[];
	strings: Record<string, string>;
}

export interface Address
{
	Components: {kind: string, name: string}[]
}

function escapeRegExp(string: string)
{
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function matchesAddressComponent(actualComponent: string, requiredComponent: string)
{
	if(requiredComponent.includes('*'))
	{
		const pattern = requiredComponent.split('*').map(escapeRegExp).join('.*');
		const regex = new RegExp(`^${pattern}$`, 'i');
		return regex.test(actualComponent);
	}
	return actualComponent === requiredComponent;
}

export function isAddressAllOf(address: Address, requiredComponents: string[])
{
	const filteredComponents = address.Components.filter(component => ["province", "area", "locality"].includes(component.kind));
	const reversedComponents = filteredComponents.map(component => component.name).reverse();

	let searchFrom = 0;
	return requiredComponents.every(requiredComponent => {
		const matchIndex = reversedComponents.findIndex((actualComponent, index) =>
			index >= searchFrom && matchesAddressComponent(actualComponent, requiredComponent)
		);
		if(matchIndex === -1) return false;
		searchFrom = matchIndex + 1;
		return true;
	});
}

// Вспомогательная функция для разбивки составных названий
function addPlaceWithSeparators(place: string, places: Set<string>) {
	// Разбиваем по разделителям < и ;
	const parts = place.split(/[<>;]/);
	parts.forEach(part => {
		const trimmed = part.trim();
		if(trimmed && trimmed !== '//') places.add(trimmed);
	});
}

// Извлечение всех уникальных названий городов из tables.json
export function extractAllPlaces(tables: Tables): Set<string> {
	const places = new Set<string>();

	// tripCost - ключи это названия городов
	Object.keys(tables.tripCost).forEach(city => {
		const parts = city.split('<');
		places.add(parts[0]); // основное название
		if(parts[1]) places.add(parts[1].trim()); // регион для уточнения
	});

	tables.warehousePlaces.forEach(place => {
		addPlaceWithSeparators(place, places);
	});

	tables.noFreeDeliveryPlaces.forEach(place => {
		addPlaceWithSeparators(place, places);
	});

	tables.halfCostDeliveryPlaces.forEach(place => {
		addPlaceWithSeparators(place, places);
	});

	tables.fullCostDeliveryPlaces.forEach(place => {
		addPlaceWithSeparators(place, places);
	});

	tables.railwayExceptions.forEach(place => {
		addPlaceWithSeparators(place, places);
	});

	Object.keys(tables.routesToFreePassingTowns).forEach(route => {
		addPlaceWithSeparators(route, places);
		tables.routesToFreePassingTowns[route].forEach(town => {
			if(typeof town === 'string') addPlaceWithSeparators(town, places);
		});
	});

	Object.keys(tables.paidRoutes).forEach(route => {
		addPlaceWithSeparators(route, places);
		Object.keys(tables.paidRoutes[route]).forEach(dest => {
			if(dest !== '//') addPlaceWithSeparators(dest, places);
		});
	});

	places.delete('//');
	places.delete('<');
	
	// Удаляем числовые значения и пустые строки
	for(const place of places)
	{
		if(/^\d+$/.test(place) || place.trim() === '')
			places.delete(place);
		if(place.startsWith('//')) places.delete(place);
	}

	tables.newRegions.forEach(place => {
		addPlaceWithSeparators(place, places);
	});

	return places;
}
